<template>
  <div class="container mx-auto min-h-screen pb-24">
    <h1
      class="w-full max-w-xl shrink-0 border-b-2 border-b-kekkle-blue bg-white py-4 text-center text-xl font-medium dark:bg-kekkle-dark-dark dark:text-slate-200"
    >
      {{ $t("pages.event.title_of") }}
      {{ groupData?.name ?? "" }}
    </h1>
    <KekkleProgressBar :active="isStale" color="blue" />
    <!-- Layout toggle + export -->
    <div class="flex items-center justify-end gap-4 p-2">
      <button
        class="flex h-10 w-10 items-center justify-center rounded-lg border-2 border-black-dark bg-white shadow-blue-hard-sm transition-all active:shadow-none dark:bg-kekkle-dark-light"
        :aria-label="
          isListMode
            ? $t('pages.event.calendar_view')
            : $t('pages.event.list_view')
        "
        @click="toggleLayout"
      >
        <CalendarIcon v-if="isListMode" class="h-5 w-5 text-kekkle-blue" />
        <ViewListIcon v-else class="h-5 w-5 text-kekkle-blue" />
      </button>
      <button
        class="flex h-10 w-10 items-center justify-center rounded-lg border-2 border-black-dark bg-white shadow-blue-hard-sm transition-all active:shadow-none dark:bg-kekkle-dark-light"
        @click="exportBottomSheetOpen = true"
      >
        <ShareIcon class="h-5 w-5 text-kekkle-blue" />
      </button>
    </div>

    <!-- Calendar view -->
    <template v-if="!isListMode">
      <div class="mx-auto p-2">
        <DatePicker
          v-model="selectedDate"
          :initial-page="initialPage"
          style="border: solid 2px black"
          translate="no"
          class="mx-auto border-2 border-solid border-black-dark shadow-blue-hard-sm dark:bg-kekkle-dark-light"
          show-iso-weeknumbers
          expanded
          :is-dark="darkModeEnabled"
          :rows="1"
          :attributes="attributes"
          @did-move="updateDate"
        />
      </div>
      <div class="p-2">
        <InformationText
          v-if="fetchError"
          color="red"
          class="mb-2"
          :information="fetchError"
        />
        <h1
          v-if="selectedDate"
          class="text-center font-medium dark:text-slate-200"
        >
          {{ $t("pages.event.selected_day") }}:
          {{ selectedDate.toLocaleDateString() }}
        </h1>
        <h1
          v-if="!selectedDate && monthInView"
          class="text-center font-medium dark:text-slate-200"
        >
          {{ $t("pages.event.selected_month") }}:
          {{ getMonth(monthInView) }}
        </h1>
        <h1
          v-if="!selectedDate && !monthInView"
          class="text-center font-medium dark:text-slate-200"
        >
          {{ $t("pages.event.no_day_selected") }}
        </h1>
        <birthday
          v-for="(birthday, index) in getActiveBirthdays"
          :key="index"
          :uuid="birthday.uuid"
          :birthday-date="birthday.birthdayDate"
          :is-today="birthday.isToday"
        ></birthday>
        <div v-if="getCurrentDayEvents.length === 0 && selectedDate">
          <p class="text-center dark:text-slate-200">
            {{ $t("pages.event.no_events_day") }}
          </p>
        </div>
        <div v-if="getCurrentDayEvents.length > 0 && selectedDate">
          <div
            v-for="(agendaItem, index) in getCurrentDayEvents"
            :key="agendaItem._id"
            class="mb-6"
          >
            <p class="dark:text-slate-200">
              {{ index + 1 }} / {{ getCurrentDayEvents.length }}
            </p>
            <agenda-item :item="agendaItem" />
          </div>
        </div>
        <div
          v-if="
            !selectedDate && getCurrentMonthEvents.length === 0 && monthInView
          "
        >
          <p class="text-center dark:text-slate-200">
            {{ $t("pages.event.no_events_month") }}
          </p>
        </div>
        <div
          v-if="
            getCurrentMonthEvents.length > 0 && !selectedDate && monthInView
          "
        >
          <div
            v-for="(agendaItem, index) in getCurrentMonthEvents"
            :key="agendaItem._id"
            class="mb-6"
          >
            <p class="dark:text-slate-200">
              {{ index + 1 }} / {{ getCurrentMonthEvents.length }}
            </p>
            <agenda-item :item="agendaItem" />
          </div>
        </div>
      </div>
    </template>

    <!-- List view -->
    <AgendaListView v-else />

    <!-- Export Calendar Bottom Sheet -->
    <export-calendar-bottom-sheet
      v-model:open="exportBottomSheetOpen"
    ></export-calendar-bottom-sheet>

    <!-- Add/Edit Event Bottom Sheets -->
    <add-event-bottom-sheet v-model:open="addEventBottomSheetOpen" />
    <edit-event-bottom-sheet
      v-model:open="editEventBottomSheetOpen"
      :event-data="selectedEventData"
    />
  </div>
</template>

<script>
import { DatePicker } from "v-calendar";
import AgendaItem from "../components/AgendaItem.vue";
import AgendaListView from "@/components/agenda/agenda-list-view.vue";
import InformationText from "../components/InformationText.vue";
import { useGroupsStore } from "@/stores/modules/groups/groups";
import { useEventsStore } from "@/stores/modules/events/events";
import Birthday from "../components/Birthday.vue";
import { getMonth } from "@/helpers/dates/get-month";
import { firebaseAnalytics } from "@/helpers/firebase/firebase";
import { useDarkModeStore } from "@/stores/modules/darkmode/darkmode";
import { AgendaLayout } from "@/enums/agenda-layout";
import { logEvent } from "firebase/analytics";
import { CalendarIcon, ViewListIcon, ShareIcon } from "@heroicons/vue/outline";
import ExportCalendarBottomSheet from "@/components/ExportCalendarBottomSheet.vue";
import { useEventManagement } from "@/composables/useEventManagement";
import AddEventBottomSheet from "@/components/AddEventBottomSheet.vue";
import EditEventBottomSheet from "@/components/EditEventBottomSheet.vue";
import KekkleProgressBar from "@/components/ui/KekkleProgressBar.vue";

export default {
  name: "Agenda",
  components: {
    Birthday,
    AgendaItem,
    AgendaListView,
    DatePicker,
    CalendarIcon,
    ViewListIcon,
    ShareIcon,
    ExportCalendarBottomSheet,
    AddEventBottomSheet,
    EditEventBottomSheet,
    InformationText,
    KekkleProgressBar,
  },
  setup() {
    const groupsStore = useGroupsStore();
    const eventsStore = useEventsStore();
    const darkModeStore = useDarkModeStore();
    const {
      addEventBottomSheetOpen,
      editEventBottomSheetOpen,
      selectedEventData,
    } = useEventManagement();
    return {
      groupsStore,
      eventsStore,
      darkModeStore,
      addEventBottomSheetOpen,
      editEventBottomSheetOpen,
      selectedEventData,
    };
  },
  data() {
    return {
      exportBottomSheetOpen: false,
      eventCollection: null,
    };
  },
  computed: {
    isListMode() {
      return this.eventsStore.layoutMode === AgendaLayout.List;
    },
    selectedDate: {
      get() {
        return this.eventsStore.selectedCalendarDate;
      },
      set(value) {
        this.eventsStore.setSelectedCalendarDate(value);
      },
    },
    monthInView: {
      get() {
        return this.eventsStore.calendarMonth;
      },
      set(value) {
        this.eventsStore.setCalendarMonthDate(value);
      },
    },
    darkModeEnabled() {
      return this.darkModeStore.darkModeEnabled;
    },
    groupData() {
      return this.groupsStore.groupData;
    },
    getCurrentMonthEvents() {
      return this.eventsStore.currentMonthEvents;
    },
    getCurrentDayEvents() {
      return this.eventsStore.currentDayEvents;
    },
    fetchError() {
      return this.eventsStore.fetchError;
    },
    isStale() {
      return this.eventsStore.isStale;
    },
    eventDates() {
      return this.eventsStore.eventDates;
    },
    rangedEventDates() {
      return this.eventsStore.rangedEventDates;
    },
    getBirthdays() {
      return this.groupsStore.birthdays;
    },
    getActiveBirthdays() {
      const today = new Date();
      const todayMonth = today.getMonth();
      const todayDate = today.getDate();

      // If a specific date is selected, show birthdays on that date
      if (this.eventsStore.selectedCalendarDate) {
        return this.getBirthdays
          .filter(
            (userData) =>
              userData.birthday &&
              typeof userData.birthday.getMonth === "function" &&
              userData.birthday.getMonth() ===
                this.eventsStore.selectedCalendarDate.getMonth() &&
              userData.birthday.getDate() ===
                this.eventsStore.selectedCalendarDate.getDate(),
          )
          .map((userData) => {
            const isToday =
              userData.birthday.getMonth() === todayMonth &&
              userData.birthday.getDate() === todayDate;
            return {
              ...userData,
              birthdayDate: userData.birthday.getDate(),
              isToday,
            };
          });
      }

      // If only a month is in view (no specific date selected), show all birthdays in that month
      if (this.monthInView) {
        return this.getBirthdays
          .filter(
            (userData) =>
              userData.birthday &&
              typeof userData.birthday.getMonth === "function" &&
              userData.birthday.getMonth() === this.monthInView.getMonth(),
          )
          .map((userData) => {
            const isToday =
              userData.birthday.getMonth() === todayMonth &&
              userData.birthday.getDate() === todayDate;
            return {
              ...userData,
              birthdayDate: userData.birthday.getDate(),
              isToday,
            };
          })
          .sort((a, b) => a.birthdayDate - b.birthdayDate);
      }

      return [];
    },
    getBirthdaysDates() {
      if (!this.monthInView) {
        return [];
      }
      return this.getBirthdays
        .filter(
          (userData) =>
            userData.birthday &&
            typeof userData.birthday.getMonth === "function" &&
            userData.birthday.getMonth() === this.monthInView.getMonth(),
        )
        .map((userData) => {
          const targetYear = this.monthInView.getFullYear();
          const birthMonth = userData.birthday.getMonth();
          const birthDay = userData.birthday.getDate();
          if (birthMonth === 1 && birthDay === 29) {
            const isLeap =
              (targetYear % 4 === 0 && targetYear % 100 !== 0) ||
              targetYear % 400 === 0;
            return new Date(targetYear, 1, isLeap ? 29 : 28);
          }
          return new Date(targetYear, birthMonth, birthDay);
        });
    },
    attributes() {
      return [
        {
          key: "today",
          highlight: {
            fillMode: "light",
            color: "orange",
          },
          dates: new Date(),
        },
        {
          key: "events",
          highlight: {
            color: "orange",
            fillMode: "solid",
          },
          dot: "orange",
          color: "orange",
          dates: this.eventDates,
        },
        {
          key: "rangedEvents",
          highlight: {
            start: { fillMode: "solid", color: "orange" },
            base: { fillMode: "light", color: "orange" },
            end: { fillMode: "solid", color: "orange" },
          },
          dot: "orange",
          dates: this.rangedEventDates,
        },
        {
          key: "birthdays",
          dot: {
            class: "birthday-content",
          },
          dates: this.getBirthdaysDates,
        },
      ];
    },
    initialPage() {
      const d = this.monthInView || new Date();
      return {
        year: d.getFullYear(),
        month: d.getMonth() + 1,
      };
    },
  },
  created() {
    this.eventsStore.subscribeToEvents();
  },
  methods: {
    toggleLayout() {
      this.eventsStore.setLayoutMode(
        this.isListMode ? AgendaLayout.Calendar : AgendaLayout.List,
      );
    },
    getMonth(date) {
      return getMonth(date);
    },
    updateDate(e) {
      if (!e || !e[0]) return;
      const page = e[0];
      const targetYear = page.year;
      const targetMonth = page.month - 1;

      const currentMonthDate = this.eventsStore.calendarMonth;
      if (
        currentMonthDate &&
        currentMonthDate.getFullYear() === targetYear &&
        currentMonthDate.getMonth() === targetMonth
      ) {
        return;
      }

      const newDate = new Date(targetYear, targetMonth, 1);
      this.eventsStore.setCalendarMonthDate(newDate);
      this.eventsStore.setSelectedCalendarDate(null);
      logEvent(firebaseAnalytics, "agenda_interaction", {
        name: "pressed_arrow",
      });
    },
  },
};
</script>
<style>
.birthday-content.vc-dot {
  background-color: transparent;
  border: none;
}

.birthday-content:before {
  font-size: 30px;
  content: "🎂" !important;
  text-align: center;
  position: absolute;
  bottom: -5px;
  transform: translateX(-50%);
}
</style>
